/**
 * Interceptor global de 401 + fetch autenticado para a API SPM.
 */
import { loginPath, normalizeAlias, readLastAlias } from "../utils/contractPath";
import { qrDataUrlFromOtpauth } from "../utils/totpQr";

export function apiBase() {
  const v = import.meta.env.VITE_API_URL;
  if (typeof v === "string" && v.trim() !== "") {
    return v.replace(/\/$/, "");
  }
  return "";
}

const TOKEN_KEY = "spm_token";
const USER_KEY = "spm_user";
const ACTIVE_COMPANY_KEY = "spm_active_company_id";
const CSRF_COOKIE = "spm_csrf";
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);
const API_PREFIXES = [
  "/auth",
  "/sca",
  "/adm",
  "/gsf",
  "/cad",
  "/ctb",
  "/cfg",
  "/portal",
  "/user",
  "/usergroup",
  "/public",
  "/financeiro",
  "/reports",
];

let unauthorizedHandler = null;
let fetchInterceptorInstalled = false;
let handlingUnauthorized = false;

const AUTH_EXEMPT_PATHS = [
  "/auth/login",
  "/auth/forgot-password/",
  "/auth/change-password",
  "/auth/logout",
];

function readStoredUserAlias() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    if (!raw) return null;
    const u = JSON.parse(raw);
    return normalizeAlias(u?.cd_alias) || null;
  } catch {
    return null;
  }
}

function clearClientCookies() {
  if (typeof document === "undefined") return;
  document.cookie = `${CSRF_COOKIE}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

export function clearAuthSession() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
  sessionStorage.removeItem(ACTIVE_COMPANY_KEY);
  clearClientCookies();
}

function readCookie(name) {
  if (typeof document === "undefined") return "";
  const parts = String(document.cookie || "").split("; ");
  for (const part of parts) {
    const i = part.indexOf("=");
    if (i < 0) continue;
    const key = decodeURIComponent(part.slice(0, i));
    if (key === name) return decodeURIComponent(part.slice(i + 1));
  }
  return "";
}

/** O cookie de sessão é HttpOnly; o CSRF (não secreto) indica que a sessão existe. */
export function hasSessionCookie() {
  return Boolean(readCookie(CSRF_COOKIE));
}

/**
 * Sinal de sessão no cliente: CSRF legível ou snapshot pós-login em sessionStorage.
 * Evita apagar o utilizador no hard reload se o CSRF ainda não for visível
 * (ex.: race); o ``/auth/me`` confirma ou limpa.
 */
export function hasClientSessionHint() {
  if (hasSessionCookie()) return true;
  if (typeof sessionStorage === "undefined") return false;
  return Boolean(sessionStorage.getItem(USER_KEY));
}

function apiPathname(input) {
  const base = apiBase();
  const url = requestUrlString(input);
  if (!url) return null;
  if (base && url.startsWith(base)) {
    return (url.slice(base.length) || "/").split("?")[0];
  }
  if (url.startsWith("/")) return url.split("?")[0];
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const parsed = new URL(url);
      if (base) {
        const origin = new URL(base, window.location.origin).origin;
        if (parsed.origin !== origin) return null;
      } else if (parsed.origin !== window.location.origin) {
        return null;
      }
      return parsed.pathname;
    } catch {
      return null;
    }
  }
  return null;
}

function isSpmApiPath(path) {
  return API_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  );
}

function applyApiSession(input, init) {
  const path = apiPathname(input);
  if (!path || !isSpmApiPath(path)) return init;
  const headers = new Headers(
    init?.headers || (typeof Request !== "undefined" && input instanceof Request
      ? input.headers
      : undefined)
  );
  const method = String(
    init?.method ||
      (typeof Request !== "undefined" && input instanceof Request ? input.method : "GET")
  ).toUpperCase();
  if (UNSAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf && !headers.has("X-CSRF-Token")) headers.set("X-CSRF-Token", csrf);
  }
  // Preferir JSON: evita fallback HTML do SPA se o proxy/API falhar.
  if (!headers.has("Accept")) headers.set("Accept", "application/json");
  return { ...(init || {}), credentials: "include", headers };
}

function requestUrlString(input) {
  if (typeof input === "string") return input;
  if (input instanceof URL) return input.href;
  if (input && typeof input.url === "string") return input.url;
  return "";
}

function isAuthenticatedApiRequest(input, init) {
  const path = apiPathname(input);
  if (!path || !isSpmApiPath(path)) return false;
  if (AUTH_EXEMPT_PATHS.some((p) => path.startsWith(p))) return false;

  const hdrs = init?.headers;
  if (hdrs instanceof Headers) {
    if (hdrs.has("Authorization")) return true;
  } else if (hdrs && typeof hdrs === "object") {
    if (hdrs.Authorization || hdrs.authorization) return true;
  }

  return hasSessionCookie() || Boolean(sessionStorage.getItem(USER_KEY));
}

export function registerUnauthorizedHandler(handler) {
  unauthorizedHandler = typeof handler === "function" ? handler : null;
}

function triggerUnauthorized() {
  if (handlingUnauthorized) return;
  handlingUnauthorized = true;
  try {
    // Encerra a sessão no servidor *antes* de apagar o CSRF legível; senão
    // fica spm_session HttpOnly órfão e o próximo /auth/login falhava (403).
    const csrf = readCookie(CSRF_COOKIE);
    const headers = new Headers({ "Content-Type": "application/json" });
    if (csrf) headers.set("X-CSRF-Token", csrf);
    void nativeLogoutFetch(headers);
    clearAuthSession();
    if (unauthorizedHandler) {
      unauthorizedHandler();
      return;
    }
    const alias = readStoredUserAlias() || readLastAlias();
    const target = loginPath(alias) || "/";
    if (typeof window !== "undefined" && window.location.pathname !== target) {
      window.location.assign(target);
    }
  } finally {
    window.setTimeout(() => {
      handlingUnauthorized = false;
    }, 500);
  }
}

/** POST /auth/logout sem passar pelo interceptor (evita reentrada no 401). */
function nativeLogoutFetch(headers) {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return Promise.resolve();
  }
  const native = window.fetch.__spmNative || window.fetch.bind(window);
  return native(`${apiBase()}/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers,
  }).catch(() => {});
}

export function installFetchUnauthorizedInterceptor() {
  if (fetchInterceptorInstalled || typeof window === "undefined") return;
  fetchInterceptorInstalled = true;
  const nativeFetch = window.fetch.bind(window);
  window.fetch.__spmNative = nativeFetch;
  window.fetch = async (input, init) => {
    const next = applyApiSession(input, init);
    const res = await nativeFetch(input, next);
    if (res.status === 401 && isAuthenticatedApiRequest(input, next)) {
      triggerUnauthorized();
    }
    return res;
  };
}

// Instalar cedo (antes de efeitos React) para Accept/credentials no proxy Vite.
if (typeof window !== "undefined") {
  installFetchUnauthorizedInterceptor();
}

export function authHeaders(extra = {}) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
    ...extra,
  };
  // Double-submit CSRF quando há sessão (mutações /auth/totp/credentials*, etc.).
  const csrf = readCookie(CSRF_COOKIE);
  if (
    csrf &&
    !headers["X-CSRF-Token"] &&
    !headers["x-csrf-token"]
  ) {
    headers["X-CSRF-Token"] = csrf;
  }
  return headers;
}

function parseError(res, data) {
  const detail = data.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  return `Erro ${res.status}`;
}

/**
 * @param {string} nm_login
 * @param {string} password
 * @param {string} cd_alias alias do contrato (path /{cdAlias})
 * @returns {Promise<{ access_token: string, token_type: string, cd_alias: string, user: object }>}
 */
export async function loginRequest(nm_login, password, cd_alias) {
  const res = await fetch(`${apiBase()}/auth/login`, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ nm_login, password, cd_alias }),
  });

  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }

  if (!res.ok) {
    throw new Error(parseError(res, data));
  }

  return data;
}

const PASSKEY_RESUME_KEY = "spm_webauthn_resume";
const PASSKEY_UI_ERROR_KEY = "spm_passkey_ui_error";

export function rememberPasskeyUiError(message) {
  const text = String(message || "").slice(0, 300);
  if (!text) return;
  try {
    sessionStorage.setItem(PASSKEY_UI_ERROR_KEY, text);
  } catch {
    /* ignore */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("spm-passkey-ui-error", { detail: text }));
  }
}

export function takePasskeyUiError() {
  try {
    const msg = sessionStorage.getItem(PASSKEY_UI_ERROR_KEY);
    if (msg) sessionStorage.removeItem(PASSKEY_UI_ERROR_KEY);
    return msg || "";
  } catch {
    return "";
  }
}

function friendlyPasskeyError(err) {
  const raw = String(err?.message || err || "");
  const name = String(err?.name || "");
  const blob = `${name} ${raw}`;
  if (/rp\.id|relying party|current origin|SecurityError/i.test(blob)) {
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    if (host === "localhost") {
      return "Não foi possível confirmar a passkey neste navegador. Tente novamente.";
    }
    return "Não foi possível confirmar a passkey neste endereço. Abra o sistema em localhost e tente novamente.";
  }
  if (/NotAllowedError|AbortError|cancel/i.test(blob)) {
    return "Confirmação da passkey cancelada.";
  }
  if (/timed out|timeout/i.test(blob)) {
    return "A confirmação da passkey expirou. Tente novamente.";
  }
  if (
    /não foi possível confirmar a passkey/i.test(raw) &&
    !/WEBAUTHN_|rp\.id/i.test(raw)
  ) {
    return raw;
  }
  return "Não foi possível confirmar a passkey. Tente novamente.";
}

async function reportPasskeyClientError(err) {
  const message = String(err?.message || err || "").slice(0, 500);
  const name = String(err?.name || "").slice(0, 80);
  try {
    await fetch(`${apiBase()}/auth/webauthn/client-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        name,
        origin: typeof window !== "undefined" ? window.location.origin : "",
      }),
    });
  } catch {
    /* a falha de rede não substitui a mensagem ao utilizador */
  }
}

function pageOrigin() {
  return typeof window !== "undefined" ? window.location.origin : "";
}

/** O navegador só aceita rp.id igual ao hostname da página aberta. */
function alignPasskeyOptions(options) {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  if (!host || !options || typeof options !== "object") return options;
  if (options.rp && typeof options.rp === "object") options.rp.id = host;
  if (typeof options.rpId === "string") options.rpId = host;
  return options;
}

async function runPasskeyCeremony(run) {
  try {
    return await run();
  } catch (err) {
    await reportPasskeyClientError(err);
    const friendly = new Error(friendlyPasskeyError(err));
    rememberPasskeyUiError(friendly.message);
    throw friendly;
  }
}

function isIpHostname(host) {
  return host === "127.0.0.1" || host === "::1" || host === "[::1]";
}

/** O navegador recusa rp.id em endereço IP. A cerimónia tem de correr em localhost. */
function redirectPasskeyToLocalhost(resume) {
  if (typeof window === "undefined" || !isIpHostname(window.location.hostname)) {
    return null;
  }
  sessionStorage.setItem(PASSKEY_RESUME_KEY, JSON.stringify(resume));
  const url = new URL(window.location.href);
  url.hostname = "localhost";
  window.location.replace(url.toString());
  return new Promise(() => {});
}

export function takePasskeyResume(kind) {
  try {
    const raw = sessionStorage.getItem(PASSKEY_RESUME_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (kind && data?.kind !== kind) return null;
    sessionStorage.removeItem(PASSKEY_RESUME_KEY);
    return data;
  } catch {
    sessionStorage.removeItem(PASSKEY_RESUME_KEY);
    return null;
  }
}

/** Completa senha + passkey. step: enroll | passkey. */
export async function finishPasskeyLogin(webauthnPendingToken, step) {
  const leaving = redirectPasskeyToLocalhost({
    kind: "login",
    token: webauthnPendingToken,
    step,
  });
  if (leaving) return leaving;

  try {
    const { startAuthentication, startRegistration } = await import(
      "@simplewebauthn/browser"
    );
    const enrolling = step === "enroll";
    const optionsPath = enrolling
      ? "/auth/webauthn/register/options"
      : "/auth/webauthn/login/options";
    const verifyPath = enrolling
      ? "/auth/webauthn/register/verify"
      : "/auth/webauthn/login/verify";
    const optionsRes = await fetch(`${apiBase()}${optionsPath}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        webauthn_pending_token: webauthnPendingToken,
        page_origin: pageOrigin(),
      }),
    });
    let options = {};
    try {
      options = await optionsRes.json();
    } catch {
      options = {};
    }
    if (!optionsRes.ok) {
      throw new Error(parseError(optionsRes, options));
    }
    alignPasskeyOptions(options);
    const credential = await runPasskeyCeremony(() =>
      enrolling
        ? startRegistration({ optionsJSON: options })
        : startAuthentication({ optionsJSON: options })
    );
    const verifyRes = await fetch(`${apiBase()}${verifyPath}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        webauthn_pending_token: webauthnPendingToken,
        page_origin: pageOrigin(),
        credential,
      }),
    });
    let data = {};
    try {
      data = await verifyRes.json();
    } catch {
      data = {};
    }
    if (!verifyRes.ok) {
      throw new Error(parseError(verifyRes, data));
    }
    return data;
  } finally {
    try {
      sessionStorage.removeItem(PASSKEY_RESUME_KEY);
    } catch {
      /* ignore */
    }
  }
}

export async function listOwnPasskeys() {
  const res = await fetch(`${apiBase()}/auth/webauthn/credentials`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return Array.isArray(data.items) ? data.items : [];
}

export async function deleteOwnPasskey(credentialId) {
  const res = await fetch(
    `${apiBase()}/auth/webauthn/credentials/${credentialId}`,
    { method: "DELETE", headers: authHeaders() }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return data;
}

export async function resetUserPasskeys(idUser) {
  const res = await fetch(
    `${apiBase()}/auth/webauthn/credentials/user/${idUser}`,
    { method: "DELETE", headers: authHeaders() }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return data;
}

/**
 * Completa senha + TOTP.
 * @param {string} totpPendingToken
 * @param {"totp"|"enroll_totp"} step
 * @param {(state: {mode: string, otpauth_uri?: string, secret?: string, qr_data_url?: string}) => Promise<string>} askCode
 *   askCode recebe estado de UI e devolve o código de 6 dígitos.
 */
export async function finishTotpLogin(totpPendingToken, step, askCode) {
  const enrolling = step === "enroll_totp";
  if (enrolling) {
    const optionsRes = await fetch(`${apiBase()}/auth/totp/enroll/options`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ totp_pending_token: totpPendingToken }),
    });
    const options = await optionsRes.json().catch(() => ({}));
    if (!optionsRes.ok) throw new Error(parseError(optionsRes, options));
    if (!options.secret || !options.otpauth_uri) {
      throw new Error(
        "Resposta de enrolamento TOTP incompleta (sem chave/QR). Reinicie o login."
      );
    }
    let qrDataUrl = options.qr_data_url || "";
    if (!qrDataUrl) {
      try {
        qrDataUrl = await qrDataUrlFromOtpauth(options.otpauth_uri);
      } catch {
        qrDataUrl = "";
      }
    }
    const code = await askCode({
      mode: "enroll",
      otpauth_uri: options.otpauth_uri,
      secret: options.secret,
      qr_data_url: qrDataUrl,
    });
    const verifyRes = await fetch(`${apiBase()}/auth/totp/enroll/verify`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        totp_pending_token: totpPendingToken,
        code: String(code || "").trim(),
      }),
    });
    const data = await verifyRes.json().catch(() => ({}));
    if (!verifyRes.ok) throw new Error(parseError(verifyRes, data));
    return data;
  }
  const code = await askCode({ mode: "verify" });
  const verifyRes = await fetch(`${apiBase()}/auth/totp/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      totp_pending_token: totpPendingToken,
      code: String(code || "").trim(),
    }),
  });
  const data = await verifyRes.json().catch(() => ({}));
  if (!verifyRes.ok) throw new Error(parseError(verifyRes, data));
  return data;
}

export async function fetchTotpStatus() {
  const res = await fetch(`${apiBase()}/auth/totp/status`, {
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return data;
}

export async function enrollSessionTotp(askCode) {
  const optionsRes = await fetch(
    `${apiBase()}/auth/totp/credentials/enroll/options`,
    { method: "POST", headers: authHeaders() }
  );
  const options = await optionsRes.json().catch(() => ({}));
  if (!optionsRes.ok) throw new Error(parseError(optionsRes, options));
  const code = await askCode({
    mode: "enroll",
    otpauth_uri: options.otpauth_uri,
    secret: options.secret,
    qr_data_url: options.qr_data_url,
  });
  const verifyRes = await fetch(
    `${apiBase()}/auth/totp/credentials/enroll/verify`,
    {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ code: String(code || "").trim() }),
    }
  );
  const data = await verifyRes.json().catch(() => ({}));
  if (!verifyRes.ok) throw new Error(parseError(verifyRes, data));
  return data;
}

export async function deleteOwnTotp() {
  const res = await fetch(`${apiBase()}/auth/totp/credentials`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return data;
}

export async function resetUserTotp(idUser) {
  const res = await fetch(
    `${apiBase()}/auth/totp/credentials/user/${idUser}`,
    { method: "DELETE", headers: authHeaders() }
  );
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(parseError(res, data));
  return data;
}

/** Regista uma passkey extra já com sessão. */
export async function enrollSessionPasskey() {
  const leaving = redirectPasskeyToLocalhost({ kind: "enroll" });
  if (leaving) return leaving;
  const { startRegistration } = await import("@simplewebauthn/browser");
  const optionsRes = await fetch(
    `${apiBase()}/auth/webauthn/credentials/register/options`,
    {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ page_origin: pageOrigin() }),
    }
  );
  const options = await optionsRes.json().catch(() => ({}));
  if (!optionsRes.ok) throw new Error(parseError(optionsRes, options));
  alignPasskeyOptions(options);
  const credential = await runPasskeyCeremony(() =>
    startRegistration({ optionsJSON: options })
  );
  const verifyRes = await fetch(
    `${apiBase()}/auth/webauthn/credentials/register/verify`,
    {
      method: "POST",
      headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ credential, page_origin: pageOrigin() }),
    }
  );
  const data = await verifyRes.json().catch(() => ({}));
  if (!verifyRes.ok) throw new Error(parseError(verifyRes, data));
  return data;
}

/** Invalida o JWT no servidor e apaga os cookies. Falha de rede não impede o logout local. */
export function logoutRequest() {
  if (!hasSessionCookie() && !sessionStorage.getItem(USER_KEY)) {
    return Promise.resolve();
  }
  return fetch(`${apiBase()}/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers: authHeaders(),
  }).catch(() => {});
}

/**
 * Redefine senha com token emitido após login (P) ou confirmação do código.
 * @param {{ password_change_token: string, new_password: string, new_password_confirm?: string }} body
 */
export async function changePasswordRequest(body) {
  const res = await fetch(`${apiBase()}/auth/change-password`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    throw new Error(parseError(res, data));
  }
  return data;
}

/**
 * Troca a senha do utilizador autenticado (senha actual + nova + confirmação).
 * @param {{ current_password: string, new_password: string, new_password_confirm: string }} body
 */
export async function changePasswordSessionRequest(body) {
  const res = await fetch(`${apiBase()}/auth/change-password/session`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    throw new Error(parseError(res, data));
  }
  return data;
}

/** Solicita código de recuperação para o e-mail cadastrado do login. */
export async function forgotPasswordRequest(nm_login, cd_alias) {
  const res = await fetch(`${apiBase()}/auth/forgot-password/request`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nm_login, cd_alias }),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    throw new Error(parseError(res, data));
  }
  return data;
}

/** Confirma código e devolve password_change_token. */
export async function forgotPasswordConfirm(nm_login, cd_alias, cd_code) {
  const res = await fetch(`${apiBase()}/auth/forgot-password/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ nm_login, cd_alias, cd_code }),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    throw new Error(parseError(res, data));
  }
  return data;
}

/**
 * Dados do usuário autenticado e contexto de empresa (BD como fonte da verdade).
 */
export async function fetchMe() {
  const res = await fetch(`${apiBase()}/auth/me`, {
    credentials: "include",
    headers: authHeaders(),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (res.status === 401) {
    const err = new Error("Sessão expirada.");
    err.code = "UNAUTHORIZED";
    throw err;
  }
  if (!res.ok) {
    throw new Error(parseError(res, data));
  }
  return data;
}
